import React, { useEffect, useMemo, useRef, useState } from 'react';
import SocialIcons from '../00-shared/SocialIcons';
import { contactEmail } from '../00-shared/constants';

/** Zoho Description (message) max length. */
const MESSAGE_MAX_LENGTH = 32000;

/** Email format same as backend validation. */
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Format up to 10 digits as (xxx) xxx-xxxx as user types. */
function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** True if string has exactly 10 digits (valid US phone for display). */
function isPhoneValid(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length === 10;
}

interface ContactFormSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: { from: string; to: string; fromBlue: string; toPurple: string };
    };
}

type FocusableFormEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * Contact form section: submits to Zoho CRM and shows success/error state.
 */
const ContactFormSection: React.FC<ContactFormSectionProps> = ({ highlightColor }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        jobTitle: '',
        phone: '',
        projectType: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const errorRef = useRef<HTMLDivElement>(null);
    const successRef = useRef<HTMLDivElement>(null);

    // Inline email validity: idle when empty, valid/invalid as user types
    const emailStatus = useMemo<'idle' | 'valid' | 'invalid'>(() => {
        const e = formData.email.trim();
        if (!e) return 'idle';
        return EMAIL_RE.test(e) ? 'valid' : 'invalid';
    }, [formData.email]);

    // Phone: valid when 10 digits (optional field; if filled, must be valid)
    const phoneStatus = useMemo<'idle' | 'valid' | 'invalid'>(() => {
        const p = formData.phone.trim();
        if (!p) return 'idle';
        return isPhoneValid(p) ? 'valid' : 'invalid';
    }, [formData.phone]);

    const canSubmit = Boolean(
        formData.firstName.trim() &&
        formData.lastName.trim() &&
        formData.email.trim() &&
        emailStatus === 'valid' &&
        (formData.phone.trim() === '' || phoneStatus === 'valid') &&
        !isSubmitting
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setIsSubmitting(true);
        setStatus('idle');
        setErrorMsg('');
        try {
            const res = await fetch('/api/contact-form/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setStatus('success');
                setFormData({ firstName: '', lastName: '', email: '', jobTitle: '', phone: '', projectType: '', message: '' });
            } else {
                setStatus('error');
                setErrorMsg(data?.error || 'Something went wrong. Please try again.');
            }
        } catch {
            setStatus('error');
            setErrorMsg('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = 'contact-form-input w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-white placeholder-gray-400 focus:outline-none';
    const focusRing = (c: string) => ({
        onFocus: (e: React.FocusEvent<FocusableFormEl>) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${c}`; },
        onBlur: (e: React.FocusEvent<FocusableFormEl>) => { e.currentTarget.style.boxShadow = ''; },
    });

    return (
        <section className="pt-32 py-20 px-4 sm:px-6 lg:px-8 bg-black relative">
            {/* White text for all fields; override Chrome autofill highlight and text color */}
            <style>{`
                .contact-form-input,
                .contact-form-input::placeholder {
                    color: rgb(255 255 255) !important;
                }
                .contact-form-input::placeholder {
                    opacity: 0.6;
                }
                .contact-form-input,
                .contact-form-input:-webkit-autofill,
                .contact-form-input:-webkit-autofill:hover,
                .contact-form-input:-webkit-autofill:focus,
                .contact-form-input:-webkit-autofill:active {
                    caret-color: rgb(255 255 255) !important;
                }
                .contact-form-input option {
                    color: rgb(255 255 255);
                    background-color: rgb(38 38 38);
                }
                .contact-form-input:-webkit-autofill,
                .contact-form-input:-webkit-autofill:hover,
                .contact-form-input:-webkit-autofill:focus,
                .contact-form-input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px rgb(23 23 23) inset !important;
                    box-shadow: 0 0 0 30px rgb(23 23 23) inset !important;
                    -webkit-text-fill-color: rgb(255 255 255) !important;
                    color: rgb(255 255 255) !important;
                }
            `}</style>
            <div className="max-w-4xl mx-auto relative z-10">
                <div className="pb-12 border-b border-neutral-800">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 text-center observe-fade opacity-0">Contact Us</h1>
                    <p className="text-gray-300 text-center observe-fade opacity-0">
                        We are here to answer any question you may have. Feel free to reach via contact form. Or email us at {contactEmail}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 observe-fade opacity-0 mt-12">
                    <div>
                        <label htmlFor="firstName" className="block text-white mb-2">First name <span className="text-red-500">*</span></label>
                        <input type="text" id="firstName" required value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} className={inputClass} disabled={isSubmitting} {...focusRing(highlightColor.primary)} />
                    </div>
                    <div>
                        <label htmlFor="lastName" className="block text-white mb-2">Last name <span className="text-red-500">*</span></label>
                        <input type="text" id="lastName" required value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} className={inputClass} disabled={isSubmitting} {...focusRing(highlightColor.primary)} />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-white mb-2">Email <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input type="email" id="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className={inputClass + ' pr-10'} disabled={isSubmitting} {...focusRing(highlightColor.primary)} />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
                                {emailStatus === 'valid' && (
                                    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                                {emailStatus === 'invalid' && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="jobTitle" className="block text-white mb-2">Job title (optional)</label>
                        <input type="text" id="jobTitle" maxLength={100} value={formData.jobTitle} onChange={e => setFormData({ ...formData, jobTitle: e.target.value })} className={inputClass} disabled={isSubmitting} placeholder="e.g. Head Coach" {...focusRing(highlightColor.primary)} />
                    </div>
                    <div>
                        <label htmlFor="phone" className="block text-white mb-2">Phone (optional)</label>
                        <div className="relative">
                            <input
                                type="tel"
                                id="phone"
                                inputMode="numeric"
                                autoComplete="tel"
                                maxLength={14}
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                                className={inputClass + ' pr-10'}
                                disabled={isSubmitting}
                                placeholder="(555) 123-4567"
                                {...focusRing(highlightColor.primary)}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
                                {phoneStatus === 'valid' && (
                                    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                                {phoneStatus === 'invalid' && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="projectType" className="block text-white mb-2">Your Team (optional)</label>
                        <select id="projectType" value={formData.projectType} onChange={e => setFormData({ ...formData, projectType: e.target.value })} className={inputClass} disabled={isSubmitting} {...focusRing(highlightColor.primary)}>
                            <option value="">Select a Team</option>
                            <option value="NCAA">NCAA</option>
                            <option value="Gym">Gym</option>
                            <option value="Clinic">Clinic</option>
                            <option value="Personal">Personal</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="message" className="block text-white mb-2">Your message (optional)</label>
                        <textarea id="message" rows={6} maxLength={MESSAGE_MAX_LENGTH} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} className={inputClass + ' resize-none'} disabled={isSubmitting} {...focusRing(highlightColor.primary)} />
                        {formData.message.length > 0 && (
                            <p className="mt-1 text-sm text-gray-500">{formData.message.length.toLocaleString()} / {MESSAGE_MAX_LENGTH.toLocaleString()}</p>
                        )}
                    </div>
                    <button type="submit" disabled={!canSubmit} className="w-full px-8 py-4 text-lg text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: highlightColor.primary }} onMouseEnter={e => { if (canSubmit) e.currentTarget.style.backgroundColor = highlightColor.hover; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = highlightColor.primary; }}>
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                    {status === 'success' && (
                        <div ref={successRef} tabIndex={-1} role="status" className="mt-6 p-4 bg-green-900/20 border border-green-500 rounded-lg outline-none" aria-live="polite">
                            <p className="text-green-400 text-center">Thank you! We'll be in touch soon.</p>
                        </div>
                    )}
                    {status === 'error' && (
                        <div ref={errorRef} tabIndex={-1} role="alert" aria-live="polite" className="mt-6 p-4 bg-red-900/20 border border-red-500 rounded-lg outline-none">
                            <p className="text-red-400 text-center">{errorMsg}</p>
                        </div>
                    )}
                </form>

                <div className="mt-16 flex justify-center"><SocialIcons /></div>
            </div>
        </section>
    );
};

export default ContactFormSection;
